<template>
  <div class="w-full min-h-screen bg-gradient-to-br from-gray-25 to-gray-50 p-2 rounded-lg">
    <div class="mb-4">
      <div class="flex items-start justify-between bg-white p-4 rounded-lg mb-2">
        <div class="ml-5">
          <div class="flex items-center gap-3 mb-2">
            <div class="w-2 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
            <h1 class="text-2xl font-bold text-gray-900 tracking-tight">فروش حضوری (POS)</h1>
          </div>
        </div>
        <button
            style="max-width: 100px"
            @click="refresh"
            :disabled="isLoadingAny"
            class="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white text-sm font-semibold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs"
            title="بروزرسانی"
        >
          <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          بروزرسانی
        </button>
      </div>

      <div class="bg-white p-2 rounded-lg mb-2">
        <div class="flex flex-wrap items-center gap-2 mb-3">
          <span class="text-[11px] text-gray-700 mr-1">بازه زمانی:</span>

          <div class="inline-flex flex-wrap items-center bg-gray-100 rounded-lg p-1 shadow-xs">
            <button
                v-for="p in presets"
                :key="p.value"
                @click="applyPreset(p.value)"
                :class="[
                'px-3 py-1.5 text-xs m-1 rounded-lg font-medium',
                (filterMode==='preset' && filters.range===p.value)
                  ? 'bg-gray-700 text-gray-50'
                  : 'bg-white text-gray-900'
              ]"
            >
              {{ p.label }}
            </button>

            <button
                @click="switchToRange"
                :class="[
                'px-3 py-1.5 text-xs m-1 rounded-lg font-medium',
                filterMode==='range'
                  ? 'bg-gray-700 text-gray-50'
                  : 'bg-white text-gray-900'
              ]"
            >
              بازه دلخواه
            </button>
          </div>
        </div>

        <div v-if="filterMode==='range'" class="flex flex-wrap items-end gap-3">
          <date-picker
              v-model="filters.from_jalali"
              type="date"
              format="jYYYY-jMM-jDD"
              display-format="jYYYY/jMM/jDD"
              placeholder="از تاریخ"
              auto-submit
              input-class="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          />
          <date-picker
              v-model="filters.to_jalali"
              type="date"
              format="jYYYY-jMM-jDD"
              display-format="jYYYY/jMM/jDD"
              placeholder="تا تاریخ"
              auto-submit
              input-class="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          />
          <button
              class="px-4 py-2 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              @click="applyRangeFilter"
              :disabled="isLoadingAny">
            اعمال فیلتر
          </button>
          <button
              class="px-3 py-2 text-xs rounded-lg border hover:bg-gray-50"
              @click="applyPreset('current_month')"
              :disabled="isLoadingAny">
            بازنشانی (ماه جاری)
          </button>
        </div>

        <p v-if="validationError" class="text-xs text-red-600 mt-2">{{ validationError }}</p>
        <p v-if="errorMessage" class="text-xs text-red-600 mt-2">{{ errorMessage }}</p>
      </div>
    </div>

    <!-- Total sales -->
    <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-6 text-center">
      <div v-if="loading.daily" class="animate-pulse">
        <div class="h-4 w-40 bg-gray-200 rounded mb-3 mx-auto"></div>
        <div class="h-12 w-64 bg-gray-200 rounded mx-auto"></div>
      </div>
      <div v-else>
        <div class="text-4xl font-bold text-gray-900 mb-1">
          {{ formatCurrency(totalSales) }}
          <span class="text-lg text-gray-500 font-normal">تومان</span>
        </div>
        <p class="text-sm text-gray-500">فروش خالص بازه (تخفیف ردیفی و تخفیف کل فاکتور کسر شده)</p>
        <div class="text-xs text-gray-400 mt-2">بازه: {{ rangeDisplay }}</div>
      </div>
    </div>

    <!-- Daily chart -->
    <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
      <h3 class="text-sm font-semibold text-gray-900 mb-4">نمودار فروش خالص روزانه</h3>
      <div v-if="loading.daily" class="animate-pulse h-24 bg-gray-200 rounded"></div>
      <div v-else-if="!dailyRows.length" class="p-6 text-center text-gray-400 text-sm">داده‌ای برای نمایش نیست</div>
      <canvas v-show="!loading.daily && dailyRows.length" height="90" id="pos-sales-daily-chart"></canvas>
    </div>

    <p v-if="excludedProfitItems" class="text-sm text-amber-800 bg-amber-50 rounded-lg p-3 mb-4" role="alert">{{ excludedProfitItems }} ردیف به دلیل نبود بهای پایه یا نرخ تاریخی معتبر در جمع سود لحاظ نشده است؛ سود نمایش‌داده‌شده برای ردیف‌های قابل محاسبه است.</p>

    <!-- Profit per currency -->
    <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
      <h3 class="text-sm font-semibold text-gray-900 mb-4">سود بازه به‌تفکیک ارز</h3>
      <div v-if="loading.profit" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div v-for="i in 4" :key="'sk'+i" class="p-4 rounded-xl bg-gray-100 animate-pulse h-20"></div>
      </div>
      <div v-else-if="!currencyProfits.length" class="p-6 text-center text-gray-400 text-sm">داده‌ای یافت نشد</div>
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
            v-for="c in currencyProfits"
            :key="c.currency_id || 'toman'"
            class="p-4 rounded-xl bg-amber-50 border border-amber-100"
        >
          <div class="text-[11px] text-amber-700 mb-1">{{ c.currency_name }}</div>
          <div class="text-lg font-bold text-gray-900">
            {{ formatNumber(c.profit_native) }}
            <span class="text-[10px] text-gray-500">{{ c.currency_symbol || c.currency_name }}</span>
          </div>
          <div class="text-xs text-gray-600 mt-2">زمان فروش: {{ formatCurrency(c.profit_toman_at_sale) }} تومان</div>
          <div class="text-xs text-gray-600 mt-1">نرخ روز: {{ formatCurrency(c.profit_toman_today) }} تومان</div>
          <div class="text-[10px] text-gray-400 mt-0.5" v-if="c.currency_id">
            نرخ تبدیل به تومان: {{ formatNumber(c.current_exchange_rate_to_toman) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Combined profit in Toman -->
    <div class="bg-emerald-50 rounded-2xl border border-emerald-200 p-8 text-center">
      <div v-if="loading.profit" class="animate-pulse">
        <div class="h-10 w-64 bg-emerald-100 rounded mx-auto"></div>
      </div>
      <div v-else>
        <div class="text-3xl font-bold text-emerald-900 mb-1">
          {{ formatCurrency(totalProfitToman) }}
          <span class="text-base text-emerald-700 font-normal">تومان</span>
        </div>
        <p class="text-sm text-emerald-700">جمع سود کل بازه، به نرخ روز</p>
        <div class="border-t border-emerald-200 mt-5 pt-5">
          <div class="text-3xl font-bold text-emerald-900 mb-1">{{ formatCurrency(totalProfitTomanAtSale) }} <span class="text-base text-emerald-700 font-normal">تومان</span></div>
          <p class="text-sm text-emerald-700">جمع سود کل بازه، به نرخ زمان فروش هر ردیف</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'

export default {
  name: 'PosSalesReport',
  data() {
    return {
      presets: [
        {value: 'today', label: 'امروز'},
        {value: 'yesterday', label: 'دیروز'},
        {value: 'current_month', label: 'ماه جاری'},
        {value: 'previous_month', label: 'ماه قبل'},
      ],
      filterMode: 'preset',
      filters: {
        range: 'current_month',
        from_jalali: '',
        to_jalali: '',
      },
      rangeLabel: '',
      fromJalali: '',
      toJalali: '',
      totalSales: 0,
      dailyRows: [],
      currencyProfits: [],
      totalProfitToman: 0,
      totalProfitTomanAtSale: 0,
      excludedProfitItems: 0,
      requestVersion: 0,
      chartInstance: null,
      loading: {
        daily: false,
        profit: false,
      },
      errorMessage: '',
      validationError: '',
    }
  },
  computed: {
    isLoadingAny() {
      return this.loading.daily || this.loading.profit
    },
    rangeDisplay() {
      if (!this.fromJalali || !this.toJalali) return this.rangeLabel || '—'
      const from = this.fromJalali.replace(/-/g, '/')
      const to = this.toJalali.replace(/-/g, '/')
      const suffix = this.rangeLabel ? ` (${this.rangeLabel})` : ''
      return `از ${from} تا ${to}${suffix}`
    },
  },
  mounted() {
    this.applyPreset('current_month')
  },
  beforeDestroy() {
    if (this.chartInstance) this.chartInstance.destroy()
  },
  methods: {
    formatCurrency(val) {
      if (val == null) return '0'
      return Number(val).toLocaleString('fa-IR')
    },
    formatNumber(val) {
      if (val == null) return '0'
      return Number(val).toLocaleString('fa-IR', {maximumFractionDigits: 2})
    },
    presetLabel(val) {
      const f = this.presets.find(p => p.value === val)
      return f ? f.label : ''
    },
    switchToRange() {
      this.filterMode = 'range'
    },
    applyPreset(preset) {
      this.filterMode = 'preset'
      this.filters.range = preset
      this.validationError = ''
      this.fetchAll()
    },
    applyRangeFilter() {
      if (this.filterMode !== 'range') return
      this.fetchAll()
    },
    refresh() {
      this.fetchAll()
    },
    buildParams() {
      const params = {}
      if (this.filterMode === 'range') {
        if (!this.filters.from_jalali || !this.filters.to_jalali) {
          this.validationError = 'هر دو تاریخ «از» و «تا» الزامی است.'
          return null
        }
        params.from_jalali = this.filters.from_jalali
        params.to_jalali = this.filters.to_jalali
      } else {
        params.range = this.filters.range || 'current_month'
      }
      this.validationError = ''
      return params
    },
    rangeLabelFromResponse(res) {
      if (res.range === 'custom') return 'سفارشی'
      return this.presetLabel(res.range) || ''
    },
    fetchAll() {
      const params = this.buildParams()
      if (!params) return
      const version = ++this.requestVersion
      this.fetchDaily(params, version)
      this.fetchProfitByCurrency(params, version)
    },
    fetchDaily(params, version) {
      this.loading.daily = true
      this.errorMessage = ''
      this.request({
        url: this.endpoint('saleReports/reports/pos/sales/daily'),
        method: 'get',
        params,
        success: (data) => {
          if (version !== this.requestVersion) return
          this.dailyRows = data.results || []
          this.totalSales = data.total || 0
          this.fromJalali = data.from_jalali || ''
          this.toJalali = data.to_jalali || ''
          this.rangeLabel = this.rangeLabelFromResponse(data)
          this.loading.daily = false
          this.$nextTick(this.renderChart)
        },
        error: () => {
          if (version !== this.requestVersion) return
          this.dailyRows = []
          this.totalSales = 0
          this.loading.daily = false
          this.errorMessage = 'خطا در دریافت فروش روزانه.'
        },
      })
    },
    fetchProfitByCurrency(params, version) {
      this.loading.profit = true
      this.request({
        url: this.endpoint('saleReports/reports/pos/profit/by-currency'),
        method: 'get',
        params,
        success: (data) => {
          if (version !== this.requestVersion) return
          this.currencyProfits = data.results || []
          this.totalProfitToman = data.total_profit_toman || 0
          this.totalProfitTomanAtSale = data.total_profit_toman_at_sale || 0
          this.excludedProfitItems = data.excluded_items_count || 0
          this.loading.profit = false
        },
        error: () => {
          if (version !== this.requestVersion) return
          this.currencyProfits = []
          this.totalProfitToman = 0
          this.totalProfitTomanAtSale = 0
          this.excludedProfitItems = 0
          this.errorMessage = 'خطا در دریافت سود بازه.'
          this.loading.profit = false
        },
      })
    },
    renderChart() {
      const ctx = document.getElementById('pos-sales-daily-chart')
      if (!ctx) return
      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }
      if (!this.dailyRows.length) return

      const labels = this.dailyRows.map(r => (r.jalali_date ? r.jalali_date.slice(5) : r.date))
      const values = this.dailyRows.map(r => r.value)

      this.chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'فروش روزانه',
              data: values,
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, .15)',
              borderWidth: 2,
              fill: true,
              tension: 0.3,
              pointRadius: 2,
            },
          ],
        },
        options: {
          responsive: true,
          legend: {display: false},
          scales: {
            xAxes: [{
              gridLines: {display: false},
            }],
            yAxes: [{
              gridLines: {color: 'rgba(161, 161, 161, .10)'},
              ticks: {
                beginAtZero: true,
                padding: 10,
                callback: (v) => Number(v).toLocaleString('fa-IR'),
              },
            }],
          },
          tooltips: {
            callbacks: {
              label: (item) => Number(item.yLabel).toLocaleString('fa-IR') + ' تومان',
            },
          },
        },
      })
    },
  },
}
</script>

<style scoped>
.shadow-xs { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
.from-gray-25 {
  --tw-gradient-from: #fcfcfd;
  --tw-gradient-to: rgba(252, 252, 253, 0);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
</style>
